import { Grid, GridColumn, GridRow, Input, ModalBottomButtons, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiHelper } from '../../../utils/apiHelper';
import { showSuccessUpdateToast } from '../../../utils/toast';

type SubstitionAppSetupFormProps = {
  appStatus?: AppStatusResponse;
  onLoad?: (isLoading: boolean) => void;
  onClose?: () => void;
};

type SubstitutionAppSetupFormType = {
  cityId: string;
  address: string;
};

export type AppStatusResponse = {
  id: string;
  name: string;
  state: string;
  country: string;
  city: string;
  premium: boolean;
  schedules: Array<{
    id: string;
    type: string;
    name: string;
    authenticationData: {
      '@class': string;
    };
  }>;
  monitors: Array<{ scheduleId: 'schulervertretungsplan' | 'lehrervertretungsplan'; publicId: string }>;
  monitorUser: {
    username: string;
    school: string;
    monitorEnabled: boolean;
    pushEnabled: boolean;
    allowPasswordReset: boolean;
  };
};

export const SubstitionAppSetupForm = ({ appStatus, onLoad, onClose }: SubstitionAppSetupFormProps) => {
  const { t } = useTranslation();

  const [cities, setCities] = useState<{ _id: string; name: string }[]>([]);

  useEffect(() => {
    onLoad && onLoad(true);
    apiHelper<{ _id: string; name: string }[]>({
      path: '/app/places',
      method: 'GET',
      type: 'json',
    }).then((r) => {
      onLoad && onLoad(false);
      setCities(r);
    });
  }, [onLoad]);

  const cityOptions: SelectOptionType[] =
    cities && cities.length > 0
      ? cities.map(
          (c) =>
            ({
              label: c.name,
              value: c._id,
              group: c.name.substring(0, c.name.indexOf(' >')),
            }) as SelectOptionType,
        )
      : [];

  const initialValues: SubstitutionAppSetupFormType = {
    cityId: appStatus ? `${appStatus.country}/${appStatus.state}/${appStatus.city}` : 'demo/demo/demo',
    address: '',
  };

  async function handleSubmit(
    values: SubstitutionAppSetupFormType,
    formHelpers: FormikHelpers<SubstitutionAppSetupFormType>,
  ) {
    onLoad && onLoad(true);

    await apiHelper({
      method: 'POST',
      path: '/app/setup',
      data: JSON.stringify(values),
      type: 'json',
    });

    showSuccessUpdateToast();

    onLoad && onLoad(false);
    formHelpers.resetForm();
    onClose && onClose();
  }

  return (
    <Formik<SubstitutionAppSetupFormType> initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ setFieldValue, values, dirty }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={6}>
                  <Select
                    name='city-id'
                    label={t('substitutions.app.cityId')}
                    onChange={async (e) => {
                      await setFieldValue('cityId', (e as SelectOptionType | undefined)?.value);
                    }}
                    options={cityOptions}
                    isSearchable
                    value={cityOptions.find((o) => o.value === values.cityId)}
                    menuPosition='fixed'
                    hasGroups
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Input
                    name='address'
                    label={t('substitutions.app.address')}
                    onChange={async (e) => {
                      await setFieldValue('address', e.target.value);
                    }}
                    value={values.address}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons closeButton={{ callback: onClose }} submitButton={{ disabled: !dirty }} />
          </Form>
        );
      }}
    </Formik>
  );
};
